import { ApiError } from '@orisini/ui/lib/api'

/**
 * Foto untuk konten (Create, editor konten): unggah, preview sebagai data
 * URL, dan pengecil sebelum unggah. Dulu bagian dari editor Studio, yang
 * dihapus 2026-11-04 karena Create + Content Bank menggantikannya.
 */

/**
 * Unggah foto. Body mentah (bukan multipart) karena Worker menyimpannya apa
 * adanya ke R2 — `api` bersama selalu mengirim JSON, jadi ini fetch sendiri.
 */
export async function uploadPhoto(blob: Blob): Promise<{ id: string; ref: string }> {
  const res = await fetch('/api/studio/assets', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': blob.type },
    body: blob,
  })
  const data = await res.json().catch(() => undefined)
  if (!res.ok) {
    const err = data?.error
    throw new ApiError(res.status, err?.code ?? 'UNKNOWN', err?.message ?? `Upload failed (${res.status})`)
  }
  return data
}

/** Foto milik user sebagai data URL, untuk preview. Di-cache per ID. */
const fotoCache = new Map<string, Promise<string>>()

export function photoDataUrl(id: string): Promise<string> {
  let p = fotoCache.get(id)
  if (!p) {
    p = fetch(`/api/studio/assets/${id}`, { credentials: 'include' })
      .then((r) => {
        if (!r.ok) throw new Error('Photo not found')
        return r.blob()
      })
      .then(blobToDataUrl)
    p.catch(() => fotoCache.delete(id))
    fotoCache.set(id, p)
  }
  return p
}

/** Isi cache dari foto yang baru saja diunggah, supaya preview tidak mengunduh ulang. */
export function rememberPhoto(id: string, dataUrl: string) {
  fotoCache.set(id, Promise.resolve(dataUrl))
}

export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(r.result as string)
    r.onerror = () => reject(r.error)
    r.readAsDataURL(blob)
  })
}

/**
 * Perkecil foto sebelum diunggah: sisi terpanjang 2160 px, JPEG 90%.
 *
 * Poster terbesar 1080×1920, jadi 2160 masih 2× untuk crop. Foto kamera 12 MP
 * (±5 MB) turun ke beberapa ratus KB — itu yang membuat preview instan dan
 * render server tidak menyeret data URL belasan MB.
 */
export async function shrinkPhoto(file: File): Promise<Blob> {
  const bmp = await createImageBitmap(file)
  const skala = Math.min(1, 2160 / Math.max(bmp.width, bmp.height))
  const w = Math.round(bmp.width * skala)
  const h = Math.round(bmp.height * skala)
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error("Your browser couldn't process this image")
  ctx.drawImage(bmp, 0, 0, w, h)
  bmp.close()
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Couldn't process this image"))), 'image/jpeg', 0.9),
  )
}
